import React, { useState, useEffect, useMemo, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Copyright } from '@/components/molecules/copyright'
import { useHistory, useLocation } from 'react-router-dom'
import logo from '@/static/images/icon/logo.svg'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import { pmsAuthMfaChallenge, pmsAuthMfaSend, pmsAuthMfaSendToHotel, pmsAuthMfaVerify } from '@/apis/aipass'
import { AxiosError } from 'axios'
import { OtpInput } from '@/components/organisms/signin/otp-input'
import { OtpMailHelpPopup } from '@/components/organisms/signin/otp-mail-help-popup'
import { notification } from 'antd'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { reportError } from '@/components/apps/error-boundary'
import { AuthContext } from '@/contexts/auth'

const OTP_LENGTH = 6
const SUPPORT_ERROR_MESSAGE = 'サポートにお問い合わせください'
const SUSPENDED_ERROR_MESSAGE = '入力回数が上限に達しました。10分後に再度お試しください'
const OTP_INVALID_ERROR_MESSAGE = '認証コードが正しくありません'
const OTP_EXPIRED_ERROR_MESSAGE = 'コードの有効期限が切れました。再送してください。'
const OTP_RESEND_WAIT_ERROR_MESSAGE = '1分経過後に再送できます'

type MfaErrorCode = 'otp_verification_failed' | 'otp_send_unavailable'
type MfaErrorReason = 'suspended' | 'expired' | 'wait_required' | 'mismatch' | 'already_verified'
type MfaErrorResponse = {
  errorCode?: string
  messageParameter?: {
    reason?: string
  }
}

const mfaErrorMessageByCode: Record<
  MfaErrorCode,
  {
    defaultMessage: string
    reasonMessages: Partial<Record<MfaErrorReason, string>>
  }
> = {
  otp_verification_failed: {
    defaultMessage: OTP_INVALID_ERROR_MESSAGE,
    reasonMessages: {
      suspended: SUSPENDED_ERROR_MESSAGE,
      expired: OTP_EXPIRED_ERROR_MESSAGE,
      mismatch: OTP_INVALID_ERROR_MESSAGE,
      already_verified: OTP_EXPIRED_ERROR_MESSAGE,
    },
  },
  otp_send_unavailable: {
    defaultMessage: SUPPORT_ERROR_MESSAGE,
    reasonMessages: {
      suspended: SUSPENDED_ERROR_MESSAGE,
      wait_required: OTP_RESEND_WAIT_ERROR_MESSAGE,
    },
  },
}

const getMfaErrorMessage = (error: AxiosError<MfaErrorResponse>, expectedErrorCode: MfaErrorCode) => {
  const responseData = error.response?.data
  if (responseData?.errorCode !== expectedErrorCode) {
    return SUPPORT_ERROR_MESSAGE
  }

  const messageBug = mfaErrorMessageByCode[expectedErrorCode]
  const reason = responseData.messageParameter?.reason as MfaErrorReason | undefined
  if (!reason) {
    return messageBug.defaultMessage
  }
  return messageBug.reasonMessages[reason] ?? messageBug.defaultMessage
}

export const VerifyOtp: React.FC<{}> = () => {
  const history = useHistory()
  const location = useLocation<{ idToken: string }>()
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setAuth } = useContext<any>(AuthContext)

  const [otpValues, setOtpValues] = useState<string[]>(Array.from({ length: OTP_LENGTH }, () => ''))
  const [formError, setFormError] = useState<string | undefined>()
  const [isBusy, setIsBusy] = useState<boolean>(false)
  const [accountEmail, setAccountEmail] = useState<string>()
  const [hotelEmail, setHotelEmail] = useState<string>()
  const [otpSendEmail, setOtpSendEmail] = useState<string>()
  const [isMailHelpPopupOpen, setIsMailHelpPopupOpen] = useState<boolean>(false)
  const [inputFocusTrigger, setInputFocusTrigger] = useState<number>(0)

  const otpCode = useMemo(() => otpValues.join(''), [otpValues])
  const isOtpComplete = useMemo(() => otpCode.length === OTP_LENGTH, [otpCode])

  useEffect(() => {
    if (!location.state?.idToken) {
      history.replace({ pathname: '/' })
      return
    }
    pmsAuthMfaChallenge(location.state.idToken)
      .then(res => {
        if (!res.mfaRequired) {
          setAuth(location.state)
          history.replace({ pathname: '/dashboard' })
          return
        }
        setAccountEmail(res.email)
        setHotelEmail(res.hotelEmail)
        setOtpSendEmail(res.email)
        return pmsAuthMfaSend(location.state.idToken).catch(e => {
          const error = e as AxiosError<MfaErrorResponse>
          setFormError(t(getMfaErrorMessage(error, 'otp_send_unavailable')))
        })
      })
      .catch(e => {
        reportError(e)
        errorHandler(e)
      })
  }, [])

  const onVerify = async () => {
    if (!isOtpComplete) {
      setFormError(t('認証コードを6桁入力してください。'))
      return
    }

    setIsBusy(true)
    try {
      await pmsAuthMfaVerify(location.state.idToken, { otp: otpValues.join('') })
      setAuth(location.state)
    } catch (e) {
      const error = e as AxiosError<MfaErrorResponse>
      setIsBusy(false)
      setFormError(t(getMfaErrorMessage(error, 'otp_verification_failed')))
    }
  }

  const onResend = async (isSendToAccount: boolean) => {
    setIsBusy(true)
    try {
      setIsMailHelpPopupOpen(false)
      if (isSendToAccount) {
        await pmsAuthMfaSend(location.state.idToken)
        setOtpSendEmail(accountEmail)
      } else {
        await pmsAuthMfaSendToHotel(location.state.idToken)
        setOtpSendEmail(hotelEmail)
      }
      notification.open({ message: t('メールを送信しました') })
      setOtpValues(Array.from({ length: OTP_LENGTH }, () => ''))
      setFormError(undefined)
      setInputFocusTrigger(prev => prev + 1)
    } catch (e) {
      const error = e as AxiosError<MfaErrorResponse>
      setFormError(t(getMfaErrorMessage(error, 'otp_send_unavailable')))
    } finally {
      setIsBusy(false)
    }
  }

  const onLogout = () => {
    history.replace({ pathname: '/' })
  }

  return (
    <>
      <div css={signinContainerStyle}>
        <div css={signinFormContainerStyle}>
          <img alt="aipass logo" src={logo} css={logoStyle} />
          <div css={otpCardStyle}>
            <p css={titleStyle}>{t('メール認証')}</p>
            <p css={emailStyle}>{otpSendEmail}</p>
            <p css={descriptionStyle}>{t('に届いた6桁のコードを入力してください。')}</p>

            <OtpInput
              values={otpValues}
              onValuesChange={values => {
                setOtpValues(values)
                setFormError(undefined)
              }}
              ariaLabelPrefix={t('認証コード')}
              focusTrigger={inputFocusTrigger}
            />

            {formError ? <div css={errorTextStyle}>{t(formError)}</div> : null}

            <Button
              buttonType={isBusy || !isOtpComplete ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              disabled={isBusy || !isOtpComplete}
              width={275}
              height={38}
              marginTop={22}
              marginLeft={'auto'}
              marginRight={'auto'}
              onClick={onVerify}
            >
              {t('認証する')}
            </Button>

            <div css={dividerStyle} />

            <button css={subLinkStyle} disabled={isBusy} onClick={() => onResend(true)}>
              {t('コードを再送する')}
            </button>
            <button css={resentTextStyle} onClick={() => setIsMailHelpPopupOpen(true)}>
              {t('メールを受け取れない場合はこちら')}
            </button>
          </div>

          <button css={backLinkStyle} onClick={onLogout}>
            {t('ログイン画面に戻る')}
          </button>
        </div>
        <div css={footerStyle}>
          <Copyright style={{ position: 'relative', marginTop: 0, bottom: 0 }} />
        </div>
      </div>

      <OtpMailHelpPopup
        isOpen={isMailHelpPopupOpen}
        hotelEmail={hotelEmail}
        onSendToHotel={() => onResend(false)}
        onCancel={() => setIsMailHelpPopupOpen(false)}
      />
    </>
  )
}

const baseTextStyle = css({
  fontSize: 12,
  color: '#272727',
  lineHeight: 1.5,
})

const signinContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  margin: '0 auto',
  backgroundImage: `url(${require('@/static/images/icon/background-pc.png')})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: '100vh',
  padding: '24px 12px 32px',
})

const signinFormContainerStyle = css({
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const logoStyle = css({
  width: 210,
  maxWidth: '70vw',
  marginBottom: 24,
})

const otpCardStyle = css({
  width: 360,
  maxWidth: '92vw',
  minHeight: 388,
  background: '#FFFFFF',
  borderRadius: '96px 96px 88px 88px',
  padding: '54px 28px 42px',
  textAlign: 'center',
  boxShadow: '0 14px 30px rgba(0, 0, 0, 0.2)',
})

const titleStyle = css(baseTextStyle, {
  fontSize: 18,
  fontWeight: 700,
  margin: 0,
})

const emailStyle = css(baseTextStyle, {
  margin: '14px 0 4px',
  fontWeight: 700,
})

const descriptionStyle = css(baseTextStyle, {
  margin: 0,
  color: '#676767',
})

const errorTextStyle = css(baseTextStyle, {
  marginTop: 14,
  color: '#D34A35',
})

const dividerStyle = css({
  width: '100%',
  height: 1,
  backgroundColor: '#EFF1F6',
  margin: '24px 0 16px',
})

const subLinkStyle = css(baseTextStyle, {
  display: 'block',
  width: '100%',
  border: 'none',
  background: 'transparent',
  color: '#f2a40b',
  fontWeight: 700,
  marginTop: 16,
  cursor: 'pointer',
  padding: 0,
  '&:hover': { opacity: 0.8 },
  '&:disabled': { cursor: 'default', opacity: 0.5 },
})

const resentTextStyle = css(subLinkStyle, {
  color: '#676767',
  fontWeight: 500,
})

const backLinkStyle = css(baseTextStyle, {
  marginTop: 24,
  border: 'none',
  background: 'transparent',
  color: '#676767',
  cursor: 'pointer',
  '&:hover': { opacity: 0.8 },
})

const footerStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
})
